import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  IActiveLearningIssue,
  IActiveLearningAnswers,
} from "../../../../../state";
import {
  Context,
  fetchActiveLearningAnswersByMaterial,
} from "../../../../../studentStore";
import Button from "../../../../../atoms/ActiveLearning/Button";
import CommentList from "../../../../../molecules/ActiveLearning/CommentList";
import MultipleTextArea from "../../../../ActiveLearning/MultipleTextArea";
import { IAnswer } from "../../../../../constants/active_learning";
import { getActiveLearningAnswers } from "../../../../../libs/ActiveLearning";
import useWaitApiCall from "../../../../../hooks/useWaitApiCall";
import styles from "../../ActiveLearningIssue.module.scss";

interface IActiveLearningIssue3Props {
  issue?: IActiveLearningIssue;
  answers: IActiveLearningAnswers[];
  setAnswers: Dispatch<SetStateAction<IActiveLearningAnswers[]>>;
}

export interface ICurrentAnswer extends IAnswer {
  good: string;
  motto: string;
}

export const Issue3 = (props: IActiveLearningIssue3Props) => {
  const { state, dispatch } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(state);
  const { material_code } = state.active_learning_data_by_lesson;
  const currentIssueNo = 3;
  const question1 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 1);
  }, [props.issue?.questions]);
  const question2 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 2);
  }, [props.issue?.questions]);
  const question3 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 3);
  }, [props.issue?.questions]);

  const [answerToQ1, setAnswerToQ1] = useState<ICurrentAnswer>({
    id: null,
    issue_no: currentIssueNo,
    question_no: question1?.order_num || NaN,
    question_id: question1?.id || NaN,
    good: "",
    motto: "",
  });
  const [answerToQ2, setAnswerToQ2] = useState<ICurrentAnswer>({
    id: null,
    issue_no: currentIssueNo,
    question_no: question2?.order_num || NaN,
    question_id: question2?.id || NaN,
    good: "",
    motto: "",
  });
  const [answerToQ3, setAnswerToQ3] = useState<ICurrentAnswer>({
    id: null,
    issue_no: currentIssueNo,
    question_no: question3?.order_num || NaN,
    question_id: question3?.id || NaN,
    good: "",
    motto: "",
  });

  useEffect(() => {
    const excludedAnswer = props.answers.filter(
      answer => answer.issue_no !== currentIssueNo,
    );
    const currentAnswers: IActiveLearningAnswers[] = [];
    [answerToQ1, answerToQ2, answerToQ3].forEach(answer => {
      currentAnswers.push({
        id: answer.id,
        issue_no: answer.issue_no,
        question_no: answer.question_no,
        question_id: answer.question_id,
        contents: {
          good: answer.good,
          motto: answer.motto,
        },
      });
    });
    props.setAnswers([...excludedAnswer, ...currentAnswers]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerToQ1, answerToQ2, answerToQ3, props.setAnswers]);

  const answersL6I2Q1 = getActiveLearningAnswers(
    state.active_learning_answers_by_material,
    material_code === "ECS001" ? 6 : 7,
    2,
    1,
  );

  const filteredAnswersByToStudentId = answersL6I2Q1.filter(
    answer => answer.to_student_id === state.student?.id,
  );

  const receivedAnswers = filteredAnswersByToStudentId.map(answer => {
    const students = state.classes.flatMap(classItem => classItem.students);
    const matchedStudent = students.find(
      student => student.id === answer.from_student_id,
    );

    return {
      student_id: matchedStudent?.id,
      full_name: `${matchedStudent?.last_name} ${matchedStudent?.first_name}`,
      contents: answer,
    };
  });

  const fetchActiveLearningAnswers = () => {
    dispatch(peep(fetchActiveLearningAnswersByMaterial(material_code)));
  };

  return (
    <div className={styles.issue}>
      <div className={styles.header}>
        <span className={styles.no}>
          WORK
          {props.issue?.no && props.issue?.no < 10
            ? `0${props.issue?.no}`
            : props.issue?.no}
        </span>
        <p className={styles.title}>{props.issue?.title}</p>
        <p className={styles.read}>
          もらったコメントを参考に自分の発表を振り返り、「問い」/「課題」/解決のアイデアについての良かった点/改善点を整理しよう。
        </p>
      </div>
      <div className={styles.commentList}>
        <div className={styles.label}>
          <p className={styles.title}>もらったコメント</p>
          <Button
            width={"158px"}
            height={"38px"}
            fontSize={"14px"}
            handleClick={() => fetchActiveLearningAnswers()}
            disabled={calling}
          >
            コメントを読み込む
          </Button>
        </div>
        <CommentList commentList={receivedAnswers} />
      </div>
      {question1 && (
        <div className={styles.themEntry}>
          <p className={styles.title}>{question1?.title}</p>
          <div className={styles.textAreaWrapper}>
            <MultipleTextArea
              activeLearningAnswers={state.active_learning_answers}
              currentIssueNo={currentIssueNo}
              question={question1}
              answer={answerToQ1}
              setAnswer={setAnswerToQ1}
              goodLabel={"良かった点"}
              mottoLabel={"改善点"}
            />
          </div>
        </div>
      )}
      {question2 && (
        <div className={styles.themEntry}>
          <p className={styles.title}>{question2?.title}</p>
          <div className={styles.textAreaWrapper}>
            <MultipleTextArea
              activeLearningAnswers={state.active_learning_answers}
              currentIssueNo={currentIssueNo}
              question={question2}
              answer={answerToQ2}
              setAnswer={setAnswerToQ2}
              goodLabel={"良かった点"}
              mottoLabel={"改善点"}
            />
          </div>
        </div>
      )}
      {question3 && (
        <div className={styles.themEntry}>
          <p className={styles.title}>{question3?.title}</p>
          <div className={styles.textAreaWrapper}>
            <MultipleTextArea
              activeLearningAnswers={state.active_learning_answers}
              currentIssueNo={currentIssueNo}
              question={question3}
              answer={answerToQ3}
              setAnswer={setAnswerToQ3}
              goodLabel={"良かった点"}
              mottoLabel={"改善点"}
            />
          </div>
        </div>
      )}
    </div>
  );
};
export default Issue3;
